import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Banner, Card } from '../../components/ui.tsx'
import { asAuthError, post } from '../../lib/api.ts'
import { useSession } from '../../lib/session.tsx'
import { AuthCard, AuthHeading } from './AuthCard.tsx'
import { GoogleButton, useAuthProviders } from './GoogleButton.tsx'
import { googleErrorMessage } from '../../../shared/auth.ts'
import type { AuthError, SessionUser } from '../../../shared/auth.ts'

/**
 * Sign in.
 *
 * Every refusal is a designed state, not a generic red box: wrong password, lockout,
 * suspension and an unavailable region each say a different thing and offer a different
 * way out. What none of them do is reveal whether the email is registered.
 *
 * While accounts are closed this screen is still served, but nothing links to it: it is
 * for the accounts the server has granted early access, and it says so rather than
 * pretending to be open. Google is not offered, since the server refuses it while closed,
 * and the way to sign up is the waitlist. Anyone else who signs in gets the same closed
 * message the server gives an unknown address.
 */
export function Login() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { setUser } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<AuthError | null>(null)

  const providers = useAuthProviders()
  // Until the answer arrives, say nothing about signing up either way — a "Start free"
  // link that turns into "Join the waitlist" a beat later looks like the product changing
  // its mind.
  const open = providers?.accountsOpen
  const closed = providers !== undefined && !providers.accountsOpen
  const next = params.get('next')
  // A Google sign-in that did not finish comes back here with a reason in the URL.
  const googleError = googleErrorMessage(params.get('error'))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const { user } = await post<{ user: SessionUser }>('/auth/login', { email, password })
      setUser(user)
      navigate(next && next.startsWith('/') ? next : '/', { replace: true })
    } catch (error) {
      setErr(asAuthError(error))
    } finally {
      setBusy(false)
    }
  }

  const badCredentials = err?.kind === 'invalid_credentials'

  return (
    <AuthCard width={400}>
      <AuthHeading
        title="Log in"
        sub={closed ? 'Early access. Accounts open more widely soon.' : 'Welcome back to your terminal.'}
      />

      {next && (
        <Banner tone="incident" title="Sign in to continue">
          You need an account to open <span className="mono">{next}</span>. We'll take you straight there.
        </Banner>
      )}

      {err?.kind === 'locked' && (
        <Banner tone="halt" icon="⛔" title="Too many failed attempts">
          For your security, sign-in is locked for this account. Try again in{' '}
          <b className="mono">{formatWait(err.retryAfterS)}</b>, or reset your password now.
          <div>
            <Link className="btn outline block sm" to="/recover" style={{ marginTop: 14 }}>
              Reset password
            </Link>
          </div>
        </Banner>
      )}

      {err?.kind === 'suspended' && (
        <Banner tone="flatten" icon="⚠" title="This account is suspended">
          {err.error} Contact <span className="mono">support@kellystake.example</span> for details.
        </Banner>
      )}

      {err?.kind === 'accounts_closed' && (
        <Banner tone="incident" title="Accounts aren't open yet">
          {err.error}
          <div>
            <Link className="btn outline block sm" to="/waitlist" style={{ marginTop: 14 }}>
              Join the waitlist
            </Link>
          </div>
        </Banner>
      )}

      {googleError && (
        <Banner tone="flatten" icon="✕" title="Google sign-in did not complete">
          {googleError}
        </Banner>
      )}

      <Card pad>
        {open && <GoogleButton next={next} label="Continue with Google" />}

        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="li-email">Email</label>
            <input
              className="input"
              type="email"
              id="li-email"
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={badCredentials ? { borderColor: 'var(--neg)' } : undefined}
            />
          </div>

          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor="li-pass">Password</label>
            <input
              className="input"
              type="password"
              id="li-pass"
              placeholder="••••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={badCredentials ? { borderColor: 'var(--neg)' } : undefined}
              aria-describedby={badCredentials ? 'li-error' : undefined}
            />
            {badCredentials && (
              <span className="help warn" id="li-error" role="alert">
                {err.error}
                {err.attemptsRemaining !== undefined &&
                  ` ${err.attemptsRemaining} attempt${err.attemptsRemaining === 1 ? '' : 's'} remaining before a temporary lockout.`}
                {/* An account created through Google has no password, and the server will
                    not say which accounts those are — telling it apart from a wrong password
                    would reveal that the address is registered. So the hint is shown to
                    everyone who gets here, which leaks nothing and still gets the Google
                    user to the button that works. */}
                {providers?.google && ' If you signed up with Google, use the button above instead.'}
              </span>
            )}
            {err && !err.kind && (
              <span className="help warn" role="alert">
                {err.error}
              </span>
            )}
          </div>

          <p className="u-sm" style={{ marginBottom: 16 }}>
            <Link to="/recover">Forgot password?</Link>
          </p>

          <button type="submit" className="btn primary block lg" disabled={busy || err?.kind === 'locked'}>
            {busy ? 'Signing in…' : 'Log in'}
          </button>

          {open && (
            <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
              New to KellyStake? <Link to="/signup">Start free</Link>
            </p>
          )}
          {closed && (
            <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
              Not on the list yet? <Link to="/waitlist">Join the waitlist</Link>
            </p>
          )}
        </form>
      </Card>

      <p className="u-center u-faint u-sm" style={{ marginTop: 18 }}>
        Accounts start in paper mode. We never hold your money.
      </p>
    </AuthCard>
  )
}

function formatWait(seconds: number | undefined): string {
  if (!seconds) return 'a few minutes'
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}
